import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Boxes, ShieldCheck } from 'lucide-react';

const formatApiError = (detail) => {
  if (!detail) return 'Authentication failed';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item?.msg || String(item)).join(' ');
  return detail.msg || 'Authentication failed';
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f5f5f7]">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#007AFF] to-[#5856D6] relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        
        <div className="relative z-10 flex flex-col items-start justify-center px-16 text-white">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-xl flex items-center justify-center mb-8">
            <Boxes className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl font-semibold tracking-tight mb-4">
            GalaxyIQ
          </h1>
          <p className="text-xl text-white/80 leading-relaxed max-w-md">
            Production-ready inventory management for modern e-commerce brands
          </p>
          
          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-10">
            {['Multi-platform', 'Real-time Analytics', 'Smart Reorder'].map((feature) => (
              <span 
                key={feature}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center">
              <Boxes className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-semibold text-black">GalaxyIQ</span>
          </div>

          <div>
            <h2 className="text-[28px] font-semibold tracking-tight text-black" data-testid="auth-title">
              Welcome back
            </h2>
            <p className="mt-2 text-[15px] text-gray-500" data-testid="login-subtitle">
              Sign in to continue to your dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[13px] font-medium text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                data-testid="email-input"
                className="h-11 rounded-xl border-gray-200 bg-white focus:border-[#007AFF] focus:ring-[#007AFF]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[13px] font-medium text-gray-700">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="password-input"
                className="h-11 rounded-xl border-gray-200 bg-white focus:border-[#007AFF] focus:ring-[#007AFF]"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[15px] font-medium rounded-xl shadow-sm active:scale-[0.98] transition-all duration-200"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div
            className="flex items-start gap-3 border-t border-gray-200 pt-5 text-left"
            data-testid="invite-only-access-notice"
          >
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#007AFF]" aria-hidden="true" />
            <p className="text-[13px] leading-5 text-gray-500">
              Access is invite-only. Contact your GalaxyIQ administrator if you need an account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
